import type { IUser } from '@on3/ui-lib/api/schema/custom-contracts';
import {
  externalApi,
  internalApi,
  proxyApi,
  proxyApiWithVary,
} from '@on3/ui-lib/utils/api';
import Cookies from 'cookies';
import jwtDecode from 'jwt-decode';
import type { NextComponentType, NextPageContext } from 'next';

import { userAccessHeader } from '../contexts/AuthProvider';
import { type NextApiParams } from '../types/next';

const withAuthentication = <P extends object>(
  WrappedComponent: NextComponentType<NextPageContext, unknown, P>,
) =>
  function WithAuthentication(props: P) {
    return <WrappedComponent {...props} />;
  };

withAuthentication.isAuth = async ({
  req,
  res,
}: NextApiParams): Promise<IUser | null> => {
  const cookies = new Cookies(req, res);

  try {
    const token = cookies.get('token');

    const refreshToken = cookies.get('refreshToken');
    // This needs to be done when requests are made
    // const expired =
    //   new Date(parseInt(jwtDecode(cookies.token).exp)).getTime() >
    //   new Date().getTime();

    if (!token) {
      if (refreshToken) {
        const { data } = await externalApi.post('users/v1/users/refresh/', {
          refreshToken,
        });

        const {
          token: newToken,
          refreshBefore: newRefreshBefore,
          refreshToken: newRefreshToken,
        } = data;
        const expires = new Date(newRefreshBefore);
        const week = new Date();

        week.setDate(week.getDate() + 7);
        if (newToken) {
          cookies.set('token', newToken, {
            expires,
            path: '/',
            httpOnly: false,
          });
          cookies.set('refreshToken', newRefreshToken, {
            expires: week,
            path: '/',
            httpOnly: false,
          });

          const Authorization = `Bearer ${newToken}`;

          const decodedUserToken: IUser = {
            ...jwtDecode(newToken),
            jwt: newToken,
            requestParams: { headers: { Authorization } },
            requestWithVaryParams: { headers: { Authorization } },
          };
          const userAccessHeaderValue = userAccessHeader(decodedUserToken);

          internalApi.defaults.headers.Authorization = Authorization;
          externalApi.defaults.headers.Authorization = Authorization;
          proxyApi.defaults.headers.Authorization = Authorization;
          proxyApiWithVary.defaults.headers.Authorization = Authorization;
          proxyApiWithVary.defaults.headers.AccessLevel = userAccessHeaderValue;

          if (decodedUserToken.requestWithVaryParams?.headers) {
            decodedUserToken.requestWithVaryParams.headers['accessLevel'] =
              userAccessHeaderValue;
          }

          return decodedUserToken;
        }

        delete internalApi.defaults.headers.Authorization;
        delete externalApi.defaults.headers.Authorization;
        delete proxyApi.defaults.headers.Authorization;
        delete proxyApiWithVary.defaults.headers.Authorization;
        delete proxyApiWithVary.defaults.headers.AccessLevel;
      } else {
        delete internalApi.defaults.headers.Authorization;
        delete externalApi.defaults.headers.Authorization;
        delete proxyApi.defaults.headers.Authorization;
        delete proxyApiWithVary.defaults.headers.Authorization;
        delete proxyApiWithVary.defaults.headers.AccessLevel;
      }
    } else {
      const Authorization = `Bearer ${token}`;
      const user: IUser = {
        ...jwtDecode(token),
        jwt: token,
        requestParams: { headers: { Authorization } },
        requestWithVaryParams: { headers: { Authorization } },
      };
      const userAccessHeaderValue = userAccessHeader(user);

      internalApi.defaults.headers.Authorization = Authorization;
      externalApi.defaults.headers.Authorization = Authorization;
      proxyApi.defaults.headers.Authorization = Authorization;
      proxyApiWithVary.defaults.headers.Authorization = Authorization;
      proxyApiWithVary.defaults.headers.AccessLevel = userAccessHeaderValue;

      if (user.requestWithVaryParams?.headers) {
        user.requestWithVaryParams.headers['accessLevel'] =
          userAccessHeaderValue;
      }

      return user;
    }

    return null;
  } catch (error) {
    console.error('withAuthentication Error: ', error);

    cookies.set('refreshToken', '', {
      expires: new Date(`Thu, 01 Jan 1970 00:00:01 GMT`),
      path: '/',
      httpOnly: false,
    });

    delete internalApi.defaults.headers.Authorization;
    delete externalApi.defaults.headers.Authorization;
    delete proxyApi.defaults.headers.Authorization;
    delete proxyApiWithVary.defaults.headers.Authorization;
    delete proxyApiWithVary.defaults.headers.AccessLevel;

    return null;
  }
};

export default withAuthentication;
export { withAuthentication };
